// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// RxJS
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HelperService } from './helper.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ProductBoxModel } from '../_models/product-box.model';

const API_BOX_CONFIGS_URL = environment.baseUrl + '/boxes';
// Real REST API
@Injectable()
export class BoxesService {
  constructor(private http: HttpClient,
    private helperService: HelperService,
    private toastr: ToastrService,
    private translate: TranslateService) { }

  getBoxConfigEnabled(): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/BoxConfigEnabled`;
    return this.http.get<boolean>(url,)
      .pipe(catchError(this.handleError<boolean>('getBoxConfigEnabled')));
  }

  getAll(defaultOnly: boolean = false): Observable<any[]> {
    let params: any = { defaultOnly: defaultOnly };
    return this.http.get<any[]>(API_BOX_CONFIGS_URL, { headers: this.helperService.getHTTPHeaders(), params: params })
      .pipe(catchError(this.handleError<any[]>('getAll')));
  }

  getProductBoxes(productIds: number[] = [], defaultOnly: boolean = false): Observable<ProductBoxModel[]> {
    let params: any = { defaultOnly: defaultOnly };
    const url = `${API_BOX_CONFIGS_URL}/ProductBoxes`;
    return this.http.patch<any[]>(url, productIds, { params: params })
      .pipe(catchError(error => {
        console.error('Error fetching product boxes:', error);
        return of([]);
      }));
  }

  createBox(box: any): Observable<any> {
    return this.http.post(API_BOX_CONFIGS_URL, box,)
      .pipe(catchError(this.handleError('createBox')));
  }

  createProductBox(productBox: any): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/ProductBoxes`;
    return this.http.post(url, productBox,)
      .pipe(catchError(this.handleError('createProductBox')));
  }

  updateBox(box: any): Observable<any> {
    return this.http.put(API_BOX_CONFIGS_URL, box,)
      .pipe(catchError(this.handleError('updateBox')));
  }

  updateProductBox(productBox: any): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/ProductBoxes`;
    return this.http.put(url, productBox,)
      .pipe(catchError(this.handleError('updateProductBox')));
  }

  setDefaultProductBox(id: number): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/ProductBoxes/${id}/SetDefault`;
    return this.http.patch(url, null,)
      .pipe(catchError(this.handleError('setDefaultProductBox')));
  }

  updateBoxConfigEnabled(isEnabled: boolean): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/BoxConfigEnabled`;
    return this.http.patch(url, null, {
      params: { isEnabled: isEnabled } as any
    })
      .pipe(catchError(this.handleError('updateBoxConfigEnabled')));
  }

  updatePrefillBoxes(isEnabled: boolean): Observable<any> {
    const url = `${API_BOX_CONFIGS_URL}/PrefillBoxesEnabled`;
    return this.http.patch(url, null, {
      params: { isEnabled: isEnabled } as any
    })
      .pipe(catchError(this.handleError('updateBoxConfigEnabled')));
  }

  deleteBox(boxId: number) {
    return this.http.delete(`${API_BOX_CONFIGS_URL}/${boxId}`,
    ).pipe(
      catchError(this.handleError('deleteBox'))
    );
  }

  deleteProductBox(boxId: number) {
    return this.http.delete(`${API_BOX_CONFIGS_URL}/ProductBoxes/${boxId}`,
    ).pipe(
      catchError(this.handleError('deleteProductBox'))
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      switch (error.status) {
        case 401:
          window.location.href = '/login';
          break;
        case 422:
          this.toastr.error(this.translate.instant('PRODUCTS.MESSAGES.ERRORS.' + error.error.reason, error.error));
          break;
        default:
          this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
      }
      return throwError(error);
    };
  }
}
